/**
 * adSurfaceMessaging — maps a `SponsoredSlot` surface key to a
 * tailored empty-state message. Renders as the fallback CTA
 * inside `AdvertiseHereCard` when no matching ad fills the slot.
 *
 * Why bother?
 *   A generic "Advertise here" prompt converts far worse than a
 *   surface-tailored one. A prospective advertiser landing on the
 *   HBCF page should see "Reach SA families researching HBCF
 *   cover this month" - not the same copy they saw on every other
 *   empty slot.
 *
 * Match order (first match wins):
 *   1. Exact surface key                     (`hbcf-adelaide-mid`)
 *   2. Prefix match, longest wins            (`suburb-*`, `pillar-*`)
 *   3. Fallback (state region only)
 */

const STATE_REGION = {
  SA: 'South Australia',
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  TAS: 'Tasmania',
  ACT: 'the ACT',
  NT: 'the NT',
};

// Convert a slug ("mount-barker") to a display name ("Mount Barker").
const humaniseSlug = (s) => {
  if (!s) return '';
  return String(s)
    .split('-')
    .filter(Boolean)
    .map((w) => (w.length <= 2 ? w.toUpperCase() : w[0].toUpperCase() + w.slice(1)))
    .join(' ');
};

// Direct look-up by full surface OR prefix. `suburb-*` prefixes get
// special treatment because they carry the suburb slug in their
// surface key.
const PREFIX_MESSAGES = [
  {
    prefix: 'hbcf-adelaide',
    forcedState: 'SA',
    title: 'Reach SA families researching builder insurance',
    body: 'This page ranks for HBCF, building indemnity insurance and QBE indemnity searches. Your business could sit right beside the answer.',
  },
  {
    prefix: 'plansa-approval',
    forcedState: 'SA',
    title: 'Reach families navigating PlanSA approvals',
    body: 'Council approvals, DAs, planning consents — SA families read this page while they are actively deciding on trades and consultants.',
  },
  {
    prefix: 'dbi-melbourne',
    forcedState: 'VIC',
    title: 'Reach Melbourne families comparing DBI cover',
    body: 'Domestic Building Insurance is a pre-contract decision. Advertise here to Victorian builders and consumers verifying cover before they sign.',
  },
  {
    prefix: 'vba-melbourne',
    forcedState: 'VIC',
    title: 'Reach Melbourne families verifying their builder',
    body: 'This page ranks for VBA registration and Domestic Builder Unlimited searches. Your Victorian brand sits right where the decision is made.',
  },
  {
    prefix: 'landscaping-adelaide',
    forcedState: 'SA',
    title: 'Reach Adelaide families planning landscaping',
    body: 'Turf, fencing, decking, retaining walls — Adelaide readers arrive with a scoped budget. Your business could be their first call.',
  },
  {
    prefix: 'landscaping-melbourne',
    forcedState: 'VIC',
    title: 'Reach Melbourne families planning landscaping',
    body: 'Turf, fencing, decking, retaining walls — Melbourne readers arrive with a scoped budget. Your business could be their first call.',
  },
  {
    prefix: 'fencing-adelaide',
    forcedState: 'SA',
    title: 'Reach SA families choosing a fencing contractor',
    body: 'Advertise here to Adelaide homeowners comparing colorbond, timber and pool fencing right at the quote stage.',
  },
  {
    prefix: 'fencing-melbourne',
    forcedState: 'VIC',
    title: 'Reach VIC families choosing a fencing contractor',
    body: 'Advertise here to Melbourne homeowners comparing colorbond, timber and pool fencing right at the quote stage.',
  },
  {
    prefix: 'driveways-adelaide',
    forcedState: 'SA',
    title: 'Reach Adelaide homeowners pricing a new driveway',
    body: 'Concrete, exposed aggregate, paving — SA readers hit this page after signing. High buying intent. Your business fits here.',
  },
  {
    prefix: 'driveways-melbourne',
    forcedState: 'VIC',
    title: 'Reach Melbourne homeowners pricing a new driveway',
    body: 'Concrete, exposed aggregate, paving — VIC readers hit this page after signing. High buying intent. Your business fits here.',
  },
  {
    prefix: 'pillar-',
    title: 'Advertise inside an evergreen pillar guide',
    body: 'Pillar pages carry the strongest organic authority on BuildPilot. Advertising here means your brand appears next to the definitive answer.',
  },
  {
    prefix: 'suburb-',
    // Custom title/body generator that pulls suburb slug out of the key.
    generator: (surface, region) => {
      const parts = surface.split('-');
      // "suburb-mount-barker-early" -> slug parts between "suburb" and position tag
      const posTags = ['early', 'mid', 'late', 'end'];
      const inner = parts.slice(1).filter((p) => !posTags.includes(p));
      const slug = inner.join('-');
      const name = humaniseSlug(slug);
      if (!name) return null;
      return {
        title: `Reach families building in ${name}`,
        body: `This page ranks locally for "${name} builders" and related searches. Local advertisers get direct access to nearby buying-intent traffic.`,
      };
    },
  },
  {
    prefix: 'question-',
    title: 'Reach families researching this question',
    body: 'Question pages capture Google\u2019s "People also ask" traffic. Buyers arrive with a specific decision on their mind - the perfect moment for a sponsor.',
  },
  {
    prefix: 'blog-',
    title: 'Advertise inside this editorial guide',
    body: 'Long-form editorial keeps readers on-page 4-5\u00d7 longer than average. High dwell time means longer viewability for your brand.',
  },
];

/**
 * Resolve messaging for a given surface + viewer state.
 * Returns { title, body, region } - `region` is derived from the
 * forcedState (if the surface is region-locked) or the viewer state.
 */
export function getSurfaceMessaging(surface = '', viewerState = null) {
  const surfaceLower = String(surface).toLowerCase();
  const viewer = viewerState ? String(viewerState).toUpperCase() : null;

  // Sort by prefix length desc so specific keys beat generic ones.
  const sorted = [...PREFIX_MESSAGES].sort((a, b) => b.prefix.length - a.prefix.length);

  for (const entry of sorted) {
    if (surfaceLower.startsWith(entry.prefix)) {
      const forced = entry.forcedState || null;
      const region = STATE_REGION[forced || viewer] || 'Australia';
      if (entry.generator) {
        const gen = entry.generator(surfaceLower, region);
        if (gen) return { ...gen, region };
      }
      return { title: entry.title, body: entry.body, region };
    }
  }

  // Fallback
  const region = STATE_REGION[viewer] || 'Australia';
  return {
    title: `Reach builders & buyers across ${region}`,
    body: 'Your business could be featured in this slot.',
    region,
  };
}
